import { usePopup } from '@/lib/PopupContext';
import { openChat } from '@/lib/chat';
import { FAQ_DATA, WHY_CHOOSE } from '@/lib/constants';
import PricingSection from '@/components/shared/PricingSection';
import TestimonialSlider from '@/components/shared/TestimonialSlider';
import ProcessSection from '@/components/shared/ProcessSection';
import FAQSection from '@/components/shared/FAQSection';
import LeadForm from '@/components/shared/LeadForm';
import { useMeta } from '@/hooks/useMeta';
import { Shield, Clock, Award, Users, FileCheck, BarChart } from 'lucide-react';

const LP_BENEFITS = [
  { icon: Shield, title: 'Legal Protection', desc: 'Nationwide trademark protection under U.S. federal law to safeguard your brand from infringement.' },
  { icon: Clock, title: 'Fast Processing', desc: 'Expedited application preparation and filing to minimize delays in securing your trademark.' },
  { icon: Award, title: 'Expert Review', desc: 'Every application is reviewed by experienced trademark specialists for accuracy and completeness.' },
  { icon: Users, title: 'Dedicated Support', desc: 'A personal case manager assigned to guide you through the entire registration process.' },
  { icon: FileCheck, title: 'Comprehensive Search', desc: 'Thorough database searches to identify potential conflicts before filing your application.' },
  { icon: BarChart, title: 'High Success Rate', desc: 'Our proven methodology and expert team deliver consistently high approval rates.' },
];

export default function LandingPage() {
  const { openPopup } = usePopup();

  useMeta({
    title: 'Register Your Trademark | Professional USPTO Filing | AmericanTrademark Firm',
    description: 'Register your trademark with confidence. Expert USPTO filing, comprehensive search, and dedicated case management. Starting at $45 + filing fee.',
    keywords: 'register trademark, trademark registration USA, USPTO filing, trademark application, brand registration, trademark search, trademark protection',
  });

  return (
    <div data-testid="landing-page">
      {/* 1. Hero Section with Form */}
      <section className="relative py-12 md:py-32 overflow-hidden" data-testid="lp-hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1770697953117-9f449ec2c07a?w=1920&q=80)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#09090B]/80 via-[#09090B]/65 to-[#09090B]/40" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-start">
            <div className="md:col-span-7">
              <p className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-[#6B9FFF] mb-3 md:mb-4">
                Trademark Registration Services
              </p>
              <h1 className="font-heading font-black text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-none text-white mb-4 md:mb-6">
                Register Your Trademark with Confidence
              </h1>
              <p className="text-base md:text-xl text-zinc-300 font-body leading-relaxed mb-6 md:mb-8 max-w-xl">
                Protect your brand identity with our professional trademark registration service. Our legal experts handle every step from comprehensive search to USPTO filing.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 max-w-xl">
                {[
                  'Comprehensive USPTO trademark search',
                  'Expert application preparation & filing',
                  'Dedicated case manager assigned to you',
                  'Full support through approval process',
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2.5">
                    <div className="w-4 h-4 md:w-5 md:h-5 border border-[#6B9FFF] flex items-center justify-center shrink-0">
                      <svg className="w-2.5 h-2.5 md:w-3 md:h-3 text-[#6B9FFF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                    </div>
                    <span className="text-xs md:text-sm text-zinc-300 font-body">{item}</span>
                  </div>
                ))}
              </div>
              <div className="mt-5 md:mt-8 flex items-center gap-4">
                <div className="h-px flex-1 bg-white/10 max-w-[140px] md:max-w-[200px]" />
                <p className="text-[10px] md:text-xs text-white font-bold font-body uppercase tracking-wider">Trusted by businesses nationwide</p>
              </div>
            </div>
            <div className="md:col-span-5">
              <div className="bg-white p-6 md:p-10 relative" data-testid="lp-hero-form">
                <div className="absolute top-0 left-0 right-0 h-1.5 bg-[#002FA7]" />
                <div className="text-center mb-5 md:mb-6">
                  <h3 className="font-heading font-bold text-xl md:text-2xl text-[#09090B] mb-1">Protect Your Brand Now</h3>
                  <p className="text-xs md:text-sm text-zinc-500 font-body">Quick & easy. No obligation. Fast response.</p>
                </div>
                <LeadForm buttonText="Start My Trademark Registration" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Info Section with Image */}
      <section className="py-14 md:py-32 bg-[#F4F4F5]" data-testid="lp-info-section">
        <div className="max-w-7xl mx-auto px-5 md:px-12">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1547371880-fac65c8600a2?w=800&q=80"
                alt="Legal document review"
                className="w-full h-[250px] md:h-[400px] object-cover"
                loading="lazy"
              />
            </div>
            <div>
              <p className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-2 md:mb-3">Why Register a Trademark?</p>
              <h2 className="font-heading font-extrabold text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight text-[#09090B] leading-tight mb-4 md:mb-6">
                Your Brand is Your Most Valuable Asset
              </h2>
              <p className="text-sm md:text-lg text-zinc-600 font-body leading-relaxed mb-3 md:mb-4">
                A registered trademark gives you exclusive legal rights to use your brand name, logo, or slogan nationwide. It prevents competitors from using confusingly similar marks and provides a strong foundation for brand growth.
              </p>
              <p className="text-sm md:text-base text-zinc-600 font-body leading-relaxed mb-6 md:mb-8">
                Without trademark protection, anyone can use your brand identity, diluting your market presence and confusing your customers. Our registration service ensures your brand is legally protected from day one.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => openPopup()}
                  className="bg-[#002FA7] text-white hover:bg-[#002480] transition-colors px-5 py-3 md:px-6 md:py-3.5 font-bold text-xs md:text-sm tracking-widest uppercase text-center"
                  data-testid="lp-info-register-btn"
                >
                  Register My Trademark
                </button>
                <button
                  onClick={openChat}
                  className="bg-transparent text-[#09090B] hover:bg-[#F4F4F5] transition-colors px-5 py-3 md:px-6 md:py-3.5 font-bold text-xs md:text-sm tracking-widest uppercase border border-[#09090B] text-center"
                  data-testid="lp-info-chat-btn"
                >
                  Chat Now
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Trademark Services Overview */}
      <section className="py-14 md:py-32 bg-white" data-testid="lp-services-section">
        <div className="max-w-7xl mx-auto px-5 md:px-12">
          <div className="text-center mb-10 md:mb-16">
            <p className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-2 md:mb-3">Our Trademark Services</p>
            <h2 className="font-heading font-extrabold text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight text-[#09090B] leading-tight">
              Complete Trademark Protection
            </h2>
          </div>
          <div className="mobile-slider md:grid-cols-2 lg:grid-cols-3 gap-0">
            {LP_BENEFITS.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} className="border border-[#E4E4E7] p-6 md:p-8" data-testid={`lp-benefit-${idx}`}>
                  <Icon className="w-7 h-7 md:w-8 md:h-8 text-[#002FA7] mb-3 md:mb-4" />
                  <h3 className="font-heading font-bold text-base md:text-lg text-[#09090B] mb-2 md:mb-3">{item.title}</h3>
                  <p className="text-xs md:text-sm text-zinc-600 font-body leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 4. Pricing Section */}
      <PricingSection />

      {/* 5. Process Section */}
      <ProcessSection />

      {/* 6. Testimonials */}
      <TestimonialSlider />

      {/* 7. Why Choose Us */}
      <section className="py-14 md:py-32 bg-white" data-testid="lp-why-choose-section">
        <div className="max-w-7xl mx-auto px-5 md:px-12">
          <div className="text-center mb-10 md:mb-16">
            <p className="text-[10px] md:text-sm font-bold uppercase tracking-[0.12em] md:tracking-[0.2em] text-[#002FA7] mb-2 md:mb-3">The AmericanTrademark Firm Difference</p>
            <h2 className="font-heading font-extrabold text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight text-[#09090B] leading-tight">
              Why Thousands Trust Us
            </h2>
          </div>
          <div className="mobile-slider md:grid-cols-2 lg:grid-cols-3 gap-0">
            {WHY_CHOOSE.map((item, idx) => (
              <div key={idx} className="border border-[#E4E4E7] p-6 md:p-8" data-testid={`lp-why-${idx}`}>
                <span className="font-heading font-black text-3xl md:text-4xl text-[#002FA7] opacity-20 block mb-2">
                  {String(idx + 1).padStart(2, '0')}
                </span>
                <h3 className="font-heading font-bold text-base md:text-lg text-[#09090B] mb-2 md:mb-3">{item.title}</h3>
                <p className="text-xs md:text-sm text-zinc-600 font-body leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8 md:mt-12">
            <button
              onClick={() => openPopup()}
              className="w-full sm:w-auto bg-[#002FA7] text-white hover:bg-[#002480] transition-colors px-6 py-3.5 md:px-8 md:py-4 font-bold text-xs md:text-sm tracking-widest uppercase text-center"
              data-testid="lp-why-register-btn"
            >
              Register My Trademark
            </button>
            <button
              onClick={openChat}
              className="w-full sm:w-auto bg-transparent text-[#09090B] hover:bg-[#F4F4F5] transition-colors px-6 py-3.5 md:px-8 md:py-4 font-bold text-xs md:text-sm tracking-widest uppercase border border-[#09090B] text-center"
              data-testid="lp-why-chat-btn"
            >
              Chat Now
            </button>
          </div>
        </div>
      </section>

      {/* 8. FAQ */}
      <FAQSection items={FAQ_DATA} />

      {/* 9. CTA Banner */}
      <section className="relative py-14 md:py-28 overflow-hidden" data-testid="lp-cta-banner">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1770697953117-9f449ec2c07a?w=1920&q=80)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#002FA7]/90 via-[#002FA7]/85 to-[#001d6a]/80" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-12 text-center">
          <h2 className="font-heading font-extrabold text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight text-white mb-3 md:mb-4 leading-tight">
            Don't Wait to Protect Your Brand
          </h2>
          <p className="text-sm md:text-lg text-white/80 font-body max-w-2xl mx-auto mb-6 md:mb-8">
            Every day without trademark protection is a risk to your business. Start your registration today and secure your brand's future.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
            <button
              onClick={() => openPopup()}
              className="w-full sm:w-auto bg-white text-[#002FA7] hover:bg-zinc-100 transition-colors px-6 py-3.5 md:px-8 md:py-4 font-bold text-xs md:text-sm tracking-widest uppercase"
              data-testid="lp-cta-register-btn"
            >
              Register My Trademark
            </button>
            <button
              onClick={openChat}
              className="w-full sm:w-auto bg-transparent text-white hover:bg-white/10 transition-colors px-6 py-3.5 md:px-8 md:py-4 font-bold text-xs md:text-sm tracking-widest uppercase border border-white"
              data-testid="lp-cta-chat-btn"
            >
              Chat Now
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
